import { useTranslation } from 'react-i18next';
import { ChevronDown, Languages } from 'lucide-react';
import { cn } from '@easymoving/ui';
import { SUPPORTED_LOCALES } from '../i18n';
import type { Locale } from '@easymoving/types';

/**
 * Language selector — mirrors the landing-site header: inline text links on
 * desktop, a native <details> dropdown on mobile. No flags; the dropdown uses
 * the warm-paper page colour (bg-bg) rather than a white card surface.
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'he').split('-')[0] as Locale;
  const change = (lng: Locale) => void i18n.changeLanguage(lng);

  return (
    <>
      {/* Desktop: inline links. */}
      <nav className="hidden sm:flex items-center gap-1.5 text-[13px]" aria-label={t('nav.language')}>
        <Languages className="size-4 text-graphite-soft me-0.5" aria-hidden="true" />
        {SUPPORTED_LOCALES.map((lng) => (
          <button
            key={lng}
            type="button"
            lang={lng}
            onClick={() => change(lng)}
            className={cn(
              'px-1.5 py-2.5 hover:text-petrol transition-colors',
              lng === current ? 'font-semibold text-ink' : 'text-graphite',
            )}
            aria-current={lng === current ? 'true' : undefined}
          >
            {t(`lang.${lng}`)}
          </button>
        ))}
      </nav>

      {/* Mobile: native <details> dropdown — warm-paper surface, no flags. */}
      <details className="relative sm:hidden">
        <summary
          className="list-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1.5 rounded-full border border-rule bg-bg px-3.5 py-2 text-[13px] font-medium cursor-pointer select-none"
          aria-label={t('nav.language')}
        >
          <Languages className="size-4 text-graphite-soft" aria-hidden="true" />
          {t(`lang.${current}`)}
          <ChevronDown className="size-3.5 text-graphite-soft" aria-hidden="true" />
        </summary>
        <div className="absolute end-0 top-full mt-1.5 z-40 min-w-36 rounded-xl border border-rule bg-bg p-1.5 elev-pop">
          {SUPPORTED_LOCALES.map((lng) => (
            <button
              key={lng}
              type="button"
              lang={lng}
              onClick={(e) => {
                change(lng);
                e.currentTarget.closest('details')?.removeAttribute('open');
              }}
              className={cn(
                'block w-full text-start rounded-lg px-3 py-2.5 text-[14px] hover:bg-surface-tint transition-colors',
                lng === current ? 'font-semibold text-ink bg-surface-tint/60' : 'text-graphite',
              )}
              aria-current={lng === current ? 'true' : undefined}
            >
              {t(`lang.${lng}`)}
            </button>
          ))}
        </div>
      </details>
    </>
  );
}
